import React, { useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import {
    FormField,
    HelpTooltip,
    INDICATION_TYPES,
    Radio,
    RadioGroup,
    TOOLTIP_POSITION,
    TooltipV2 as Tooltip,
} from 'taboola-ultimate-ui';
import { withIndication } from 'modules/errors';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import useFormValidatedValue from 'modules/taboola-common-frontend-modules/validations/hooks/useFormValidatedValue';
import { BID_STRATEGIES } from '../../config';
import CONVERSION_RULE_MODES from '../../config/ConversionRuleModes';
import { useBidStrategyFormField } from '../BidStrategyV2/hooks/useBidStrategyFormField';
import { CampaignConversionRules } from './CampaignConversionRules/CampaignConversionRules';
import { ConversionGoalTitle } from './ConversionGoalTitle';
import { ConversionOptimizationDropDown } from './ConversionOptimizationDropDown';
import ConversionRulesTooltip from './ConversionRulesTooltip';
import { conversionRulesValidations } from './ConversionRulesValidations';
import { useClcInitialData } from './hook/useClcInitialData';
import { useConversionRulesMode } from './hook/useConversionRulesMode';
import { useConversionRulesValidationDependencies } from './hook/useConversionRulesValidationDependencies';
import { useIsConversionRuleDisable } from './hook/useIsConversionRuleDisable';
import commonStyles from '../commonEditor.module.scss';
import styles from './conversionOptimization.module.scss';

const FormFieldWithIndication = withIndication(FormField);

export const ConversionRules = () => {
    const { formatMessage } = useIntl();
    const [hasOptions, setHasOptions] = useState(false);
    const isCampaignConversionRulesEnabled = useConfigMatch({
        [FEATURE_FLAGS.CAMPAIGN_MULTIPLE_CONVERSIONS_ENABLED]: 'true',
    });
    const isCampaignNonOptimizedConversionRulesEnabled = useConfigMatch({
        [FEATURE_FLAGS.CAMPAIGN_NON_OPTIMIZED_CONVERSIONS_ENABLED]: 'true',
    });
    const showAccountDefault = useConfigMatch({ [FEATURE_FLAGS.HIDE_CONVERSIONS_GOAL_ACCOUNT_DEFAULT]: 'false' });

    const {
        value: conversionRulesMode,
        onChange: onConversionRulesModeChange,
        mode,
        failedValidationData: conversionRuleModeFiledValidations,
    } = useConversionRulesMode();
    const { value: bidStrategy } = useBidStrategyFormField();

    const {
        optimizedConversionRulesInitialData: selectedOptimizedConversionRules,
        nonOptimizedConversionRulesInitialData: selectedNonOptimizedConversionRules,
        accountDefaultRule,
    } = useClcInitialData();

    const validationDependenciesOptimizedConversionRules = useConversionRulesValidationDependencies({
        selectedRules: selectedOptimizedConversionRules,
        mode,
        isCampaignConversionRulesEnabled,
        conversionRulesMode,
    });

    const validationDependenciesNonOptimizedConversionRules = useConversionRulesValidationDependencies({
        selectedRules: selectedNonOptimizedConversionRules,
        mode,
        isCampaignConversionRulesEnabled,
        conversionRulesMode,
    });

    const {
        value: optimizedConversionRules,
        onChange: onChangeOptimizedConversionRules,
        indicationData,
        scrollRef,
    } = useFormValidatedValue({
        field: 'conversionRules.optimizedRules',
        validations: conversionRulesValidations(true),
        validationDependencies: validationDependenciesOptimizedConversionRules,
    });

    const { value: nonOptimizedConversionRules, onChange: onChangeNonOptimizedConversionRules } = useFormValidatedValue(
        {
            field: 'conversionRules.nonOptimizedRules',
            validations: conversionRulesValidations(isCampaignNonOptimizedConversionRulesEnabled),
            validationDependencies: validationDependenciesNonOptimizedConversionRules,
        }
    );

    const { indicationType } = indicationData;
    const { indicationType: conversionRuleModeIndicationType } = conversionRuleModeFiledValidations;

    const onChangeConversionRuleMode = useCallback(
        mode => {
            if (mode === CONVERSION_RULE_MODES.DEFAULT) {
                onChangeOptimizedConversionRules([]);
                onChangeNonOptimizedConversionRules([]);
            }
            onConversionRulesModeChange(mode);
        },
        [onChangeOptimizedConversionRules, onChangeNonOptimizedConversionRules, onConversionRulesModeChange]
    );

    const isAllowedMaxConversionsBidStrategy = useConfigMatch({
        [FEATURE_FLAGS.CAMPAIGN_LEVEL_CONVERSIONS_BID_STRATEGY_ALLOW_MAX_CONVERSIONS]: 'true',
    });

    const isMaxConversions = BID_STRATEGIES.MAX_CONVERSIONS === bidStrategy && !isAllowedMaxConversionsBidStrategy;
    const disabled = useIsConversionRuleDisable();

    const memoizedOptimizedConversionRules = useMemo(() => optimizedConversionRules || [], [optimizedConversionRules]);
    const memoizedNonOptimizedConversionRules = useMemo(
        () => nonOptimizedConversionRules || [],
        [nonOptimizedConversionRules]
    );

    return (
        <>
            <FormFieldWithIndication
                inputId="conversionRules"
                ref={scrollRef}
                label={
                    <div className={styles['label-container']}>
                        <FormattedMessage
                            id="campaign.editor.conversion.goal.title"
                            defaultMessage="Conversion Goal'"
                        />
                        <HelpTooltip content={<ConversionRulesTooltip />} />
                    </div>
                }
                description={
                    <FormattedMessage
                        id="campaign.editor.conversion.goal.description"
                        defaultMessage="Select “Conversion events” if you want SmartBid’s algorithm to optimize your campaign towards a different conversion than the account default."
                    />
                }
                containerClass={styles['list']}
                indicationContainerClass={
                    indicationType === INDICATION_TYPES.WARNING ||
                    conversionRuleModeIndicationType === INDICATION_TYPES.WARNING
                        ? commonStyles['warning-indication']
                        : null
                }
                {...indicationData}
                {...conversionRuleModeFiledValidations}
            >
                {showAccountDefault && (
                    <RadioGroup
                        name="conversionRuleRadioSelected"
                        selectedValue={conversionRulesMode}
                        onChange={onChangeConversionRuleMode}
                    >
                        <Radio
                            value={CONVERSION_RULE_MODES.DEFAULT}
                            title={
                                <div>
                                    <FormattedMessage
                                        id="campaign.editor.conversion.goal.account.default"
                                        defaultMessage="Account Default"
                                    />
                                    {accountDefaultRule && <span>{`: ${accountDefaultRule}`}</span>}
                                    <HelpTooltip
                                        iconClassName={styles['help-icon']}
                                        position={TOOLTIP_POSITION.RIGHT}
                                        content="Choose “Account default” to keep the campaign optimizing towards the conversion events that are  already Included in Total under the Tracking tab."
                                    />
                                </div>
                            }
                        />
                        <Radio
                            value={CONVERSION_RULE_MODES.CUSTOM}
                            title={<ConversionGoalTitle hasOptions={hasOptions} />}
                            disabled={disabled || !hasOptions}
                        />
                    </RadioGroup>
                )}
                <div className={styles['container']}>
                    {!isCampaignConversionRulesEnabled ? (
                        <ConversionOptimizationDropDown
                            conversionRules={memoizedOptimizedConversionRules}
                            onChangeConversionRules={onChangeOptimizedConversionRules}
                            setHasOptions={setHasOptions}
                            showAccountDefault={showAccountDefault}
                        />
                    ) : (
                        <>
                            <CampaignConversionRules
                                conversionRules={memoizedOptimizedConversionRules}
                                onChangeConversionRules={onChangeOptimizedConversionRules}
                                setHasOptions={setHasOptions}
                                disabled={disabled}
                                onChangeConversionRuleMode={onChangeConversionRuleMode}
                                disabledConversionRules={memoizedNonOptimizedConversionRules}
                                message={formatMessage({
                                    id: 'campaign.editor.conversion.goal.placeholder',
                                    defaultMessage: 'Select Conversion...',
                                })}
                                fieldName="conversionRules.optimizedRules"
                                isOtherConversionRules={!isEmpty(memoizedNonOptimizedConversionRules)}
                            />
                            {isCampaignNonOptimizedConversionRulesEnabled && (
                                <div className={styles['non-conversion-section']}>
                                    <div className={styles['non-conversion-description']}>
                                        <FormattedMessage
                                            id="campaign.editor.non.conversion.goal.description"
                                            defaultMessage='Select "Conversion events" that will be appear only in reports'
                                        />
                                    </div>
                                    <CampaignConversionRules
                                        conversionRules={memoizedNonOptimizedConversionRules}
                                        onChangeConversionRules={onChangeNonOptimizedConversionRules}
                                        setHasOptions={setHasOptions}
                                        disabled={disabled}
                                        onChangeConversionRuleMode={onChangeConversionRuleMode}
                                        disabledConversionRules={memoizedOptimizedConversionRules}
                                        message={formatMessage({
                                            id: 'campaign.editor.conversion.non.optimized.placeholder',
                                            defaultMessage: 'Select conversion for report only...',
                                        })}
                                        fieldName="conversionRules.nonOptimizedRules"
                                        isOtherConversionRules={!isEmpty(memoizedOptimizedConversionRules)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {isMaxConversions && (
                        <Tooltip arrow interactive delay={800}>
                            <FormattedMessage
                                id="campaign.editor.conversion.goal.disabled.maxConversions.tooltip"
                                defaultMessage="Conversion events are not supported for Max Conversions Campaigns. To enable it you can select a
                        different bidding strategy in step 2."
                            />
                        </Tooltip>
                    )}
                </div>
            </FormFieldWithIndication>
        </>
    );
};
